import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Stack, TextField, Button, Switch, FormControlLabel,
  IconButton, CircularProgress, Alert, Divider,
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, Save as SaveIcon } from '@mui/icons-material';
import { externalTechnicianService, ExternalTechnician } from '../services/api';

const emptyForm = {
  name: '',
  company: '',
  phone: '',
  email: '',
  specialty: '',
  notes: '',
  is_active: true,
};

const Technicians: React.FC = () => {
  const [items, setItems] = useState<ExternalTechnician[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await externalTechnicianService.getAll();
      setItems(res.data.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load technicians.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await externalTechnicianService.update(editingId, form);
      } else {
        await externalTechnicianService.create(form);
      }
      setForm(emptyForm);
      setEditingId(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to save technician.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (tech: ExternalTechnician) => {
    setEditingId(tech.id);
    setForm({
      name: tech.name || '',
      company: tech.company || '',
      phone: tech.phone || '',
      email: tech.email || '',
      specialty: tech.specialty || '',
      notes: tech.notes || '',
      is_active: tech.is_active ?? true,
    });
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this technician?')) return;
    try {
      await externalTechnicianService.delete(id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to delete technician.');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        External Technicians
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Add real repair contacts (garages/technicians) and assign them to maintenance/interventions.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <TextField label="Company / Garage" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField fullWidth label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <TextField fullWidth label="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Stack>
            <TextField label="Specialty" value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} />
            <TextField label="Notes" multiline rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            <FormControlLabel
              control={<Switch checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />}
              label="Active"
            />
            <Stack direction="row" spacing={1}>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving || !form.name.trim()}>
                {editingId ? 'Update technician' : 'Add technician'}
              </Button>
              {editingId && (
                <Button variant="text" onClick={() => { setEditingId(null); setForm(emptyForm); }}>
                  Cancel
                </Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Technician list</Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
          ) : items.length === 0 ? (
            <Typography color="text.secondary">No technicians yet.</Typography>
          ) : (
            <Stack divider={<Divider flexItem />} spacing={1}>
              {items.map((tech) => (
                <Box key={tech.id} sx={{ py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography fontWeight={600}>
                      {tech.name} {!tech.is_active ? '(inactive)' : ''}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {[tech.company, tech.phone, tech.email].filter(Boolean).join(' • ') || '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <IconButton onClick={() => handleEdit(tech)}><EditIcon /></IconButton>
                    <IconButton color="error" onClick={() => handleDelete(tech.id)}><DeleteIcon /></IconButton>
                  </Box>
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Technicians;

